/**
 * workerBridge — the thin PURE adapter the real agent-worker client
 * (src/lib/agent/argoWorker.ts) imports to brand-govern its prompts.
 *
 * One function, no IO, no store, no clock: given a scope, a token budget,
 * and a BrandBrain value, return everything the worker seam needs —
 * the composed fragment to prepend to the system prompt, the provenance
 * meta to stamp on the reply, and the human verify badge. Deterministic:
 * same input → same output, and it NEVER throws (the fallback path is
 * inherited from buildBrandSystemFragment), so wiring it into the worker
 * can never block or break generation.
 */

import type { BrandBrain, BrandScope } from './schema';
import {
  brandBadge,
  brandProvenanceMeta,
  buildBrandSystemFragment,
  type BrandProvenanceMeta,
} from './inject';

export interface BrandInjectionInput {
  scope: BrandScope;
  tokenBudget: number;
  brain: BrandBrain;
}

export interface BrandInjection {
  /** Prompt fragment to prepend to the system prompt ('' on fallback). */
  fragment: string;
  /** Envelope fields to attach to the output's provenance. */
  provenanceMeta: BrandProvenanceMeta;
  /** Human verify badge, e.g. 'Brand Brain applied (3 directives, hash …)'. */
  badge: string;
}

/**
 * Compose the brand injection for one generation call. Pure + total:
 * never throws, deterministic for stable input.
 */
export function brandInjectionHook(input: BrandInjectionInput): BrandInjection {
  const result = buildBrandSystemFragment(input.brain, input.scope, input.tokenBudget);
  return {
    fragment: result.fragment,
    provenanceMeta: brandProvenanceMeta(result),
    badge: brandBadge(result),
  };
}
